import {
  readLocalStorageObject,
  writeLocalStorageObject,
  readLocalStorageRaw,
  writeLocalStorageRaw,
  removeLocalStorageItem,
} from '../sizerStorage';
import type {
  ISizerPopupAhu,
  ISizerPopupProject,
  ISizerPopupSection,
  ISizerPopupTunnel,
} from 'app/services/sizerPopupApi';

/**
 * Phase 4e — the FOUR `localStorage` keys owned by the selection popup itself.
 *
 * `sizerStorage.ts` (4b) owns the three the sizer PAGE writes; discovery §4a
 * lists these four as "header-owned, not sizer". They are still a shared
 * contract, now between React tabs: `GlobalHeader` renders the popup on every
 * board (driven by the singleton in `sizerSelectionHost.ts`, the React shape of
 * `header.js:202-229`), so a `/sizer/` tab and a board tab must see one
 * coherent state. Every write below stays byte-identical to legacy
 * `fn.local_storage.set` (see the note atop `sizerStorage.ts`), via the
 * primitives re-exported from there.
 */

export const SIZER_POPUP_STATE_KEY = 'sizer_popup_state';
export const SIZER_POPUP_FIELDS_STATUS_KEY = 'sizer_popup_fields_status';
export const SIZER_DOESNT_NEED_UPDATE_KEY = 'sizer_doesnt_need_update';
export const DRAGGABLE_POPUP_KEY = 'draggable-popup';

/** `AVAILABLE_STATES` (sizer_popup.js:52-57). */
export const POPUP_STATES = {
  INITIAL: 'initial',
  OPENED: 'opened',
  MINIMIZED: 'minimized',
  CLOSED: 'closed',
} as const;
export type SizerPopupState = (typeof POPUP_STATES)[keyof typeof POPUP_STATES];

/**
 * `sizer_popup_fields_status` — the popup's entire memory, one flat bag.
 *
 * Known keys (sizer_popup.js:296-335 `syncChanges` + the loaders):
 *  - `projects` / `pinned_projects` — the CACHED `[{id,name}]` lists. Also
 *    written from OUTSIDE the popup, by the whiteboard/dashboard app
 *    (`whiteboard/mvc/collection/projects.js:93-102`,
 *    `whiteboard/mvc/view/project.js:356-358`) so the popup opens warm.
 *  - `project_select_value` / `project_select_name`
 *  - `ahus`, `ahu_select_value`
 *  - `tunnels`, `tunnel_{id}_checkbox_value` (boolean)
 *  - `sections` (`{[tunnel_id]: Section[]}`), `section_{3..11}_select_value`
 * `syncChanges` derives every `*_value` key as `element.id + '_value'`, so the
 * naming is mechanical and unknown keys must round-trip untouched.
 */
export interface SizerPopupFieldsStatus {
  projects?: ISizerPopupProject[];
  pinned_projects?: ISizerPopupProject[];
  project_select_value?: string | number | '';
  project_select_name?: string;
  ahus?: ISizerPopupAhu[];
  ahu_select_value?: string | number | '';
  tunnels?: ISizerPopupTunnel[];
  sections?: Record<string, ISizerPopupSection[]>;
  [key: string]: unknown;
}

export function readFieldsStatus(): SizerPopupFieldsStatus | null {
  return readLocalStorageObject<SizerPopupFieldsStatus>(SIZER_POPUP_FIELDS_STATUS_KEY);
}

export function writeFieldsStatus(value: SizerPopupFieldsStatus): void {
  writeLocalStorageObject(SIZER_POPUP_FIELDS_STATUS_KEY, value);
}

/** A scalar — `fn.local_storage.set` stores strings as-is, and `get`'s `{` sniff
 * means it comes back raw (sizerStorage.ts's contract note 2). */
export function readPopupState(): SizerPopupState | null {
  const raw = readLocalStorageRaw(SIZER_POPUP_STATE_KEY);
  return (raw as SizerPopupState | null) ?? null;
}

export function writePopupState(state: SizerPopupState): void {
  writeLocalStorageRaw(SIZER_POPUP_STATE_KEY, state);
}

export function removePopupState(): void {
  removeLocalStorageItem(SIZER_POPUP_STATE_KEY);
}

/**
 * `sizer_doesnt_need_update` — set BEFORE the unit POST when sections are part
 * of the apply and cleared once the tunnel POSTs resolve (sizer_popup.js:861,
 * :876). In legacy its readers were the header's socket-driven refreshers
 * (`forceUpdateSections/AHUs/Tunnels/Project`, header.js:418-463), which bail
 * while it is set so an in-flight apply is not clobbered by a syncdata echo.
 * The React reader is the controller's post-apply refresh
 * (`scheduleForceUpdateAfterApply` → `readDoesntNeedUpdate()` below).
 * ⚠ `localStorage.setItem(key, true)` stores the STRING `"true"`.
 */
export function setDoesntNeedUpdate(): void {
  writeLocalStorageRaw(SIZER_DOESNT_NEED_UPDATE_KEY, 'true');
}

export function removeDoesntNeedUpdate(): void {
  removeLocalStorageItem(SIZER_DOESNT_NEED_UPDATE_KEY);
}

/** `localStorage.getItem('sizer_doesnt_need_update')` — the truthiness test the
 * legacy refreshers gate on (`header.js:432`, `:448`, `:458`). */
export function readDoesntNeedUpdate(): boolean {
  return Boolean(readLocalStorageRaw(SIZER_DOESNT_NEED_UPDATE_KEY));
}

/** The dragged position of `#draggable-popup`, written on every drag tick
 * (sizer_popup.js:469-477) and read back on open (:452-468). */
export interface DraggablePopupPosition {
  top?: number | string;
  left?: number | string;
  'z-index'?: string;
}

/**
 * ⚠ Read with a BARE `JSON.parse`, guarded only by the two string checks legacy
 * uses (`localStorage['draggable-popup'] && !== "undefined"`) — because
 * `close_popup` sets the key to the STRING `"undefined"` rather than removing it
 * (:761). A malformed value yields `null` here instead of throwing.
 */
export function readDraggablePosition(): DraggablePopupPosition | null {
  const raw = readLocalStorageRaw(DRAGGABLE_POPUP_KEY);
  if (!raw || raw === 'undefined') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as DraggablePopupPosition) : null;
  } catch {
    return null;
  }
}

export function writeDraggablePosition(value: DraggablePopupPosition): void {
  writeLocalStorageRaw(DRAGGABLE_POPUP_KEY, JSON.stringify(value));
}

/** `localStorage["draggable-popup"] = undefined` (:761) — assigning the VALUE
 * `undefined` to a localStorage property stringifies it, so the key survives
 * holding the literal text `"undefined"`. Reproduced, not "fixed": `open_popup`
 * explicitly tests for that string. */
export function clearDraggablePosition(): void {
  writeLocalStorageRaw(DRAGGABLE_POPUP_KEY, 'undefined');
}
